import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery'
import { useOrgState } from '../context/contextHooks';
import {getSelectedOrg, getPaymentTypes, getSelectedType} from "./homeFunctions";
import { useStyles } from "../styles/styles";
import useTheme from "@mui/material/styles/useTheme";
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

const Options = (props) => {
    const courtStates = props.courtStates;
    const getOrgs = props.getOrgs;
    const orgs = props.orgs;
    const court = props.court;
    const setSelectedOrg = props.setSelectedOrg;
    const showNext = props.showNext;
    const setActionResponse = props.setActionResponse;
    const classes = useStyles;
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [orgState, setOrgState] = useOrgState();
    const [pymtTypes, setPymtTypes] = useState(null);
    const [selectedType, setSelectedType] = useState(null);

    const handleState = (e) => {
        const state = e.target.value;
        if (!state || state === '') {
            setOrgState(null);
            getOrgs(null);
            setPymtTypes(null);
        } else if (!orgState || state !== orgState) {
            setOrgState(state);
            getOrgs(state);
            setPymtTypes(null);
        }
    }

    const handleOrg = (e) => {
        const orgId = e.target.value;
        if (orgId) {
            if (!court || court.courtId !== orgId) {
                const org = getSelectedOrg(orgs, orgId);
                setSelectedOrg(org);
                if (org.inactive) {
                    setActionResponse({
                        error: true,
                        errorMessage: `The ${org.courtName} is currently performing maintenance, and online payments are not available at this time.  Please check back later.`
                    })
                } else {
                    setActionResponse(null);
                    setPymtTypes(getPaymentTypes(org));
                }
            }
        } else {
            setActionResponse(null);
        }
    }

    const handlePaymentType = (e) => {
        const typeKey = e.target.value;
        if (typeKey) {
            if (!selectedType || selectedType.key !== typeKey) {
                const paymentType = getSelectedType(pymtTypes, typeKey);
                setSelectedType(paymentType);
            }
        } else {
            setSelectedType(null);
        }
    }

    const showSelection = () => {
        showNext(selectedType);
    }

    useEffect(() => {
        if (pymtTypes && pymtTypes.length === 1) {
            setSelectedType(pymtTypes[0]);
        } else {
            setSelectedType(null);
        }
    }, [pymtTypes])

    return (
        <>
            <Grid container spacing={3} justifyContent='center' role="presentation">
                <Grid item xs={1}>&nbsp;</Grid>
                <Grid item xs={10}>
                    { smallScreen ?
                        <Typography
                            variant='subtitle2'
                            color='primary'
                            sx={{...classes.title, marginTop: '1rem', lineHeight: '1.2rem'}}
                        >
                            To make a payment to a court or government agency, enter the agency's state
                            and name, and indicate what you want to pay for.
                        </Typography> :
                        <Typography variant={smallScreen ? 'h6' : 'h5'} component="h2" sx={classes.title}>
                            Choose who and what to pay
                        </Typography>
                    }
                    <Typography
                        variant='subtitle2'
                        color='textSecondary'
                        sx={{marginTop: '.5rem', lineHeight: '1.2rem'}}
                    >
                        As you make each selection, the next choice will appear below.
                    </Typography>
                    <FormControl fullWidth sx={{marginTop: '1rem'}}>
                        <FormLabel htmlFor='orgState'>
                            <Typography variant="subtitle1" component="span" color='primary'>
                                State
                            </Typography>
                        </FormLabel>
                        <Select
                            id='orgState'
                            name='orgState'
                            native variant='outlined'
                            fullWidth
                            defaultValue=''
                            onChange={handleState}
                            inputProps={{ 'aria-label': 'State' }}
                            sx={{height: '2rem'}}
                        >
                            <option key='none' value=''>None Selected</option>
                            { courtStates.map((state) => (
                                <option key={state.key} value={state.key}>{state.name}</option>
                            ))}
                        </Select>
                    </FormControl>
                    {orgState && orgs &&
                        <FormControl fullWidth sx={{marginTop: '1rem'}}>
                            <FormLabel htmlFor='court'>
                                <Typography variant="subtitle1" component="span" color='primary'>
                                    Organization
                                </Typography>
                            </FormLabel>
                            <Select
                                id='court'
                                name='court'
                                native variant='outlined'
                                fullWidth
                                onChange={handleOrg}
                                inputProps={{ 'aria-label': 'Organization' }}
                                sx={{height: '2rem'}}
                            >
                                <option key='none' value=''>None Selected</option>
                                {orgs.map((org) => (
                                    <option key={org.courtId} value={org.courtId}>{org.courtName}</option>
                                ))}
                            </Select>
                        </FormControl>
                    }
                    { (court && !court.inactive) && pymtTypes && pymtTypes.length > 1 &&
                        <FormControl fullWidth sx={{marginTop: '1rem'}}>
                            <FormLabel htmlFor='paymentType'>
                                <Typography variant="subtitle1" component="span" color='primary'>
                                    Payment Type
                                </Typography>
                            </FormLabel>
                            <Select
                                id='paymentType'
                                name='paymentType'
                                native variant='outlined'
                                fullWidth
                                onChange={handlePaymentType}
                                inputProps={{ 'aria-label': 'Payment Type' }}
                                sx={{height: '2rem'}}
                            >
                                <option key='none' value=''>None Selected</option>
                                { pymtTypes.map((type) => (
                                    <option key={type.key} value={type.key}>{type.description}</option>
                                ))}
                            </Select>
                        </FormControl>
                    }
                    {selectedType &&
                        <Box component='div' sx={{...classes.root, marginTop: '1rem'}}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddShoppingCartIcon/>}
                                fullWidth
                                sx={classes.button}
                                onClick={showSelection}
                            >
                                {selectedType.description}
                            </Button>
                        </Box>
                    }
                </Grid>
                <Grid item xs={1}>&nbsp;</Grid>
            </Grid>
        </>
    );
}

export default Options;
